import { Suspense, useEffect, useRef } from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type {
  MyCommentReaction,
  ShowroomComment,
  UnseenComment,
} from "../../../api/comment";
import type { ShowroomEntry } from "../../../api/showroom";
import type { SmallTeamMember } from "../../../api/smallTeam";
import type { SparkId } from "../../../api/spark";
import type { MeResponse } from "../../../api/user";
import type { WorkshopId, WorkshopUserRole } from "../../../api/workshop";
import { Spinner } from "../../../design-system/utils/Spinner/Spinner.jsx";
import PageShell from "../../../design-system/components/PageShell/PageShell";
import ReactButton, {
  legacyReactions,
  type LegacyReaction,
} from "../../../design-system/primitives/components/ReactButton/ReactButton";
import Divider from "../../../design-system/primitives/components/Divider/Divider";
import Text from "../../../design-system/primitives/components/Text/Text";
import { pluralize } from "../../../lib/text-utils.js";
import cx from "../../../lib/cx.js";
import { linkify } from "../../../lib/linkify";
import { preLine } from "../../../lib/text.css";
import { OtherFormFields } from "./ShowroomEntryParts";
import Comments from "./Comments";
import {
  EntryMedia,
  ProjectLinkButton,
  type CoverFile,
  type Upload,
} from "./UploadContent";
import styles from "./FullShowroomEntryWithData.css";

// The special form fields the entry reads. `formData` is untyped since its keys
// are driven by each spark's form definition.
type EntryFormData = {
  title?: string;
  description?: string;
  tags?: string[];
  upload?: Upload;
  coverImage?: CoverFile;
};

const Tags = ({ tags }: { tags?: string[] }) =>
  tags && tags.length > 0 ? (
    <ul className={styles.tags}>
      {tags.map((tag) => (
        <li key={tag} className={styles.fieldValue}>
          #{tag}
        </li>
      ))}
    </ul>
  ) : null;

const reactionKeys = Object.keys(legacyReactions) as LegacyReaction[];

type ReactionBarProps = {
  entry: ShowroomEntry;
  myReaction: string | null;
  sparkId?: SparkId;
};

const ReactionBar = ({ entry, myReaction, sparkId }: ReactionBarProps) => {
  const [doAdd] = useMutation(MUTATIONS.addReaction);
  const [doRemove] = useMutation(MUTATIONS.removeReaction);
  const toggleReaction = (reaction: string, isActive: boolean) => {
    const input = {
      reaction,
      workshopId: entry.workshopId,
      entryId: entry.id,
      smallTeamId: entry.smallTeamId,
      sparkId,
    };
    void (isActive ? doRemove(input) : doAdd(input));
  };

  return (
    <div className={styles.reactionBar}>
      <ul className={styles.reactionsList}>
        {reactionKeys.map((r) => {
          const isActive = myReaction === r;
          return (
            <li key={r}>
              <ReactButton
                legacy
                reaction={r}
                selected={isActive}
                count={entry.reactionCounts[r] || 0}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReaction(r, isActive);
                }}
              />
            </li>
          );
        })}
      </ul>
      <div className={styles.visitCount} aria-live="polite">
        {pluralize(entry.visitCounts, "Besuch", "Besuche")}
      </div>
    </div>
  );
};

const useVisit = ({
  entry,
  sparkId,
}: {
  entry: ShowroomEntry;
  sparkId?: SparkId;
}) => {
  const [doVisit] = useMutation(MUTATIONS.addVisit);
  const latestEntryRef = useRef({ ...entry, sparkId });
  useEffect(() => {
    latestEntryRef.current = { ...entry, sparkId };
  }, [entry, sparkId]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void doVisit({
        workshopId: latestEntryRef.current.workshopId,
        entryId: latestEntryRef.current.id,
        smallTeamId: latestEntryRef.current.smallTeamId,
        sparkId: latestEntryRef.current.sparkId,
      });
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [doVisit]);
};

/**
 * Everything the comment section needs, as one bundle — so an entry shown
 * without comments (the demo, or a workshop that switched them off) leaves it
 * out entirely rather than handing in a viewer and five empty lists.
 */
export type CommentSection = {
  comments: ShowroomComment[];
  myCommentReactions: MyCommentReaction[];
  me: MeResponse;
  /**
   * The workshop the viewer is a member of, which is what makes a comment
   * markable as seen. Absent on the public showroom, where the viewer is
   * outside the Lernreise the entry belongs to.
   */
  workshopId?: WorkshopId | null;
  unseenComments?: UnseenComment[];
  /** The viewer's roles in that workshop; an admin may delete any comment. */
  myRoles?: readonly WorkshopUserRole[];
  /** Lets signed-out visitors comment behind a captcha — the public showroom. */
  isPublic?: boolean;
};

type FullShowroomEntryWithDataProps = {
  entry: ShowroomEntry;
  teamMembers: SmallTeamMember[];
  myReaction: string | null;
  /** The comment section, or nothing where the entry is shown without one. */
  commentSection?: CommentSection | null;
  sparkId?: SparkId;
  backPath: string;
};

export const FullShowroomEntryWithData = ({
  entry,
  teamMembers,
  myReaction,
  commentSection,
  sparkId,
  backPath,
}: FullShowroomEntryWithDataProps) => {
  const { title, upload, coverImage, tags, description } =
    entry.formData as EntryFormData;
  useVisit({ entry, sparkId });

  return (
    <Suspense fallback={<Spinner />}>
      {/* The entry itself is the page, so the shell carries no title row: only
          the close button in the sheet's corner, over the starfield. */}
      <PageShell
        surface="dark"
        cornerButton={{
          icon: "x",
          ariaLabel: "Schließen",
          // Replaces the entry in the history rather than stacking the showroom
          // on top of it, so browsing entry after entry doesn't leave a chain
          // of showroom/entry pairs behind the browser's own back button.
          to: backPath,
          replace: true,
        }}
      >
        <article className={styles.innerContainer}>
          <EntryMedia upload={upload} coverImage={coverImage} />
          <div className={styles.contentWrapper}>
            <header className={styles.headerRow}>
              {title && (
                <Text variant="subheader" as="h1">
                  {title}
                </Text>
              )}
              <div className={styles.headerAction}>
                <ProjectLinkButton upload={upload} />
              </div>
            </header>
            <Tags tags={tags} />

            {teamMembers.length > 0 && (
              <section
                className={styles.mainInfoCol}
                aria-labelledby="team-label"
              >
                <Text variant="label-large" as="h2" id="team-label">
                  Projekt von
                </Text>
                <Text>{teamMembers.map((u) => u.name).join(", ")}</Text>
              </section>
            )}

            <OtherFormFields formData={entry.formData} styles={styles} />

            {description && (
              <section
                className={styles.mainInfoCol}
                aria-labelledby="description-label"
              >
                <Text variant="label-large" as="h2" id="description-label">
                  Projektbeschreibung
                </Text>
                {/* Not `<Text>`: linkify returns elements, not a string. */}
                <p className={cx(styles.fieldValue, preLine)}>
                  {linkify(description)}
                </p>
              </section>
            )}

            <Divider />

            <section className={styles.sectionSpacing} aria-label="Reaktionen">
              <ReactionBar
                entry={entry}
                myReaction={myReaction}
                sparkId={sparkId}
              />
            </section>

            {commentSection && (
              <section aria-label="Kommentare">
                <Comments
                  {...commentSection}
                  myRoles={commentSection.myRoles ?? []}
                  unseenComments={commentSection.unseenComments ?? []}
                  entryId={entry.id}
                  smallTeamId={entry.smallTeamId}
                  sparkId={sparkId}
                />
              </section>
            )}
          </div>
        </article>
      </PageShell>
    </Suspense>
  );
};
